
/**
 * normalizeData - normalizes merged testData+legacyVitals into a consistent shape.
 */
function normalizeData(raw) {
  const d = { ...raw };

  if (!d.bloodGlucose && (d.bloodGlucoseFasting || d.bloodGlucoseRandom || d.hba1c)) {
    d.bloodGlucose = {
      fasting: d.bloodGlucoseFasting?.value ?? d.bloodGlucoseFasting ?? null,
      random: d.bloodGlucoseRandom?.value ?? d.bloodGlucoseRandom ?? null,
      hba1c: d.hba1c?.value ?? d.hba1c ?? null,
      unit: d.bloodGlucoseFasting?.unit || 'mg/dL'
    };
  }

  if (!d.spirometry && d.spirometryTest) {
    d.spirometry = {
      fvc: d.spirometryTest.fvc?.value ?? d.spirometryTest.fvc,
      fev1: d.spirometryTest.fev1?.value ?? d.spirometryTest.fev1,
      pef: d.spirometryTest.pef?.value ?? d.spirometryTest.pef,
      fev1Fvc: d.spirometryTest.fev1_fvc?.value ?? d.spirometryTest.fev1_fvc,
      fef25: d.spirometryTest.fef25?.value ?? d.spirometryTest.fef25,
      fef50: d.spirometryTest.fef50?.value ?? d.spirometryTest.fef50,
      fef75: d.spirometryTest.fef75?.value ?? d.spirometryTest.fef75,
      fef2575: d.spirometryTest.fef25_75?.value ?? d.spirometryTest.fef25_75,
      status: d.spirometryTest.status
    };
  }

  const hasNestedBodyComp = d.bodyComposition &&
    Object.values(d.bodyComposition).some(v => v !== null && v !== undefined);
  if (!hasNestedBodyComp && (d.bodyFatPercentage || d.muscleMass || d.boneMass || d.bmr)) {
    d.bodyComposition = {
      bodyFatPercentage:        d.bodyFatPercentage?.value         ?? d.bodyFatPercentage,
      muscleMass:               d.muscleMass?.value                ?? d.muscleMass,
      boneMass:                 d.boneMass?.value                  ?? d.boneMass,
      bodyWater:                d.bodyWaterPercentage?.value       ?? d.bodyWaterPercentage,
      visceralFat:              d.visceralFat?.value               ?? d.visceralFat,
      bmr:                      d.bmr?.value                       ?? d.bmr,
      subcutaneousFat:          d.subcutaneousFat?.value           ?? d.subcutaneousFat,
      proteinPercentage:        d.proteinPercentage?.value         ?? d.proteinPercentage,
      skeletalMusclePercentage: d.skeletalMusclePercentage?.value  ?? d.skeletalMusclePercentage,
      bodyScore:                d.bodyScore?.value                 ?? d.bodyScore,
      metabolicAge:             d.metabolicAge?.value              ?? d.metabolicAge,
    };
  }

  d._waist = d.waistCircumference?.value ?? d.waistCircumference ?? null;
  d._waistUnit = d.waistCircumference?.unit || 'cm';
  d._hip = d.hipCircumference?.value ?? d.hipCircumference ?? null;
  d._hipUnit = d.hipCircumference?.unit || 'cm';
  if (d._waist && d._hip) d._whr = (d._waist / d._hip).toFixed(2);

  return d;
}

// ── Zone bar: colored segments with patient value marked ─────────────────────
// zones: [{label, textColor, bgColor, max}] — covers scaleMin to last zone.max
function buildZoneBar(value, scaleMin, scaleMax, zones) {
  const totalRange = scaleMax - scaleMin;
  const pct = Math.min(99, Math.max(1, ((value - scaleMin) / totalRange) * 100));

  let segHtml = '';
  let labelHtml = '';
  let prev = scaleMin;

  zones.forEach((zone, i) => {
    const segWidth = Math.max(0.5, ((zone.max - prev) / totalRange) * 100);
    const isLast = i === zones.length - 1;
    segHtml += `<div style="width:${segWidth}%;background:${zone.bgColor};height:100%;flex-shrink:0;${isLast ? '' : 'border-right:2px solid #fff'}"></div>`;
    labelHtml += `<div style="width:${segWidth}%;text-align:center;font-size:8px;font-weight:700;color:${zone.textColor};padding-top:3px;overflow:hidden;white-space:nowrap">${zone.label}</div>`;
    prev = zone.max;
  });

  return `
  <div style="position:relative;margin:8px 0 2px">
    <div style="display:flex;width:100%;height:11px;border-radius:6px;overflow:hidden">
      ${segHtml}
    </div>
    <div style="position:absolute;top:-3px;left:${pct}%;transform:translateX(-50%);width:17px;height:17px;border-radius:50%;background:#1e293b;border:2.5px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,0.45);z-index:3;-webkit-print-color-adjust:exact;print-color-adjust:exact"></div>
  </div>
  <div style="display:flex;width:100%;margin-bottom:10px">${labelHtml}</div>`;
}

// ── Metric card — accepts optional zones for labeled color-scale bar ──────────
function metricCard(label, desc, value, unit, idealLow, idealHigh, scaleMin, scaleMax, moderateHigh, higherIsBetter, alwaysNormal, zones) {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';

  const displayNum = Number.isInteger(num) ? num : parseFloat(num.toFixed(2));

  let statusText, statusColor;
  if (zones) {
    const zone = zones.find(z => num <= z.max) || zones[zones.length - 1];
    statusText  = zone.label;
    statusColor = zone.textColor;
  } else if (alwaysNormal) {
    statusText  = 'Normal';
    statusColor = '#16a34a';
  } else if (higherIsBetter) {
    if (num < idealLow)        { statusText = 'Low';       statusColor = '#dc2626'; }
    else if (num <= idealHigh) { statusText = 'Normal';    statusColor = '#16a34a'; }
    else                       { statusText = 'Excellent'; statusColor = '#16a34a'; }
  } else {
    const isNormal   = num >= idealLow && num <= idealHigh;
    const isModerate = moderateHigh ? (num > idealHigh && num <= moderateHigh) : false;
    const isHigh     = moderateHigh ? num > moderateHigh : num > idealHigh;
    statusText  = isNormal ? 'Normal' : isModerate ? 'Moderate' : isHigh ? 'High' : 'Low';
    statusColor = (isNormal || isModerate) ? '#16a34a' : '#dc2626';
  }

  let barHtml;
  if (zones) {
    barHtml = buildZoneBar(num, scaleMin, scaleMax, zones);
  } else {
    const pct      = Math.min(100, Math.max(0, ((num - scaleMin) / (scaleMax - scaleMin)) * 100));
    const isNormal    = num >= idealLow && num <= idealHigh;
    const isExcellent = higherIsBetter && num > idealHigh;
    const barColor = (alwaysNormal || isNormal || isExcellent) ? '#86E000' : '#dc2626';
    barHtml = `
    <div class="bar-track">
      <div class="bar-fill" style="width:${pct}%;background:${barColor};-webkit-print-color-adjust:exact;print-color-adjust:exact"></div>
      <div class="bar-dot" style="left:${pct}%;background:${statusColor}"></div>
    </div>`;
  }

  return `
  <div class="mc">
    <div class="mc-name">${label}</div>
    <div class="mc-desc">${desc}</div>
    <div class="mc-row">
      <div>
        <div class="mc-rlabel">Your Result</div>
        <div class="mc-value">${displayNum}<span class="mc-unit"> ${unit}</span></div>
      </div>
      <div class="mc-range">
        <div class="mc-rlabel" style="text-align:right">Ideal Range</div>
        <div class="mc-range-val">${idealLow} – ${idealHigh} ${unit}</div>
      </div>
    </div>
    ${barHtml}
    <div class="mc-status" style="color:${statusColor}">${statusText}</div>
  </div>`;
}

// ── Actual value card — green bar with dot, "Actual" label, no ideal range ───
function actualValueCard(label, desc, value, unit, scaleMin, scaleMax) {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  const displayNum = Number.isInteger(num) ? num : parseFloat(num.toFixed(1));
  const pct = Math.min(99, Math.max(1, ((num - scaleMin) / (scaleMax - scaleMin)) * 100));
  return `
  <div class="mc">
    <div class="mc-name">${label}</div>
    <div class="mc-desc">${desc}</div>
    <div class="mc-row">
      <div>
        <div class="mc-rlabel">Your Result</div>
        <div class="mc-value">${displayNum}<span class="mc-unit"> ${unit}</span></div>
      </div>
    </div>
    <div class="bar-track">
      <div class="bar-fill" style="width:${pct}%;background:#86E000;-webkit-print-color-adjust:exact;print-color-adjust:exact"></div>
      <div class="bar-dot" style="left:${pct}%;background:#16a34a"></div>
    </div>
    <div class="mc-status" style="color:#16a34a">Actual</div>
  </div>`;
}

// ── Lab test card (no progress bar) ──────────────────────────────────────────
function labCard(label, desc, result) {
  if (!result) return '';
  const lower = String(result).toLowerCase();
  const isNeg = lower === 'negative' || lower === 'non-reactive' || lower === 'n/a' || lower === 'not done';
  const isPos = lower === 'positive' || lower === 'reactive';
  const badgeColor = isNeg ? '#16a34a' : isPos ? '#dc2626' : '#d97706';
  const badgeBg    = isNeg ? '#dcfce7' : isPos ? '#fee2e2' : '#fef3c7';
  const statusText = isNeg ? 'Normal' : isPos ? 'Abnormal' : 'Review';

  return `
  <div class="mc">
    <div class="mc-name">${label}</div>
    <div class="mc-desc">${desc}</div>
    <div style="margin-top:10px">
      <div class="mc-rlabel">Result</div>
      <div class="mc-value" style="font-size:15px;margin-top:2px">${result}</div>
    </div>
    <div style="margin-top:10px;text-align:right">
      <span style="display:inline-block;padding:3px 14px;border-radius:20px;font-size:11px;font-weight:700;background:${badgeBg};color:${badgeColor}">${statusText}</span>
    </div>
  </div>`;
}

// ── Overall respiratory score from FEV1/FVC ───────────────────────────────────
function computeRespiratoryGrade(fev1Fvc, fvc, fev1) {
  const fvcNum  = parseFloat(fvc);
  const fev1Num = parseFloat(fev1);
  if (!isNaN(fvcNum)  && fvcNum  < 1.5) return { label: 'Severe Impairment', color: '#dc2626', bg: '#fee2e2', detail: 'Critically low lung volumes detected. Consult a pulmonologist.' };
  if (!isNaN(fev1Num) && fev1Num < 1.5) return { label: 'Severe Impairment', color: '#dc2626', bg: '#fee2e2', detail: 'Critically low lung volumes detected. Consult a pulmonologist.' };
  const v = parseFloat(fev1Fvc);
  if (isNaN(v)) return null;
  if (v >= 75) return { label: 'Normal', color: '#16a34a', bg: '#dcfce7', detail: 'Overall lung function is within the normal range.' };
  if (v >= 60) return { label: 'Mild Impairment', color: '#d97706', bg: '#fef3c7', detail: 'Slight reduction in airflow. Monitor and follow up.' };
  if (v >= 50) return { label: 'Moderate Impairment', color: '#ea580c', bg: '#ffedd5', detail: 'Moderate reduction in lung airflow. Medical review advised.' };
  return { label: 'Severe Impairment', color: '#dc2626', bg: '#fee2e2', detail: 'Significant lung function reduction. Consult a pulmonologist.' };
}

// ── Overall score ─────────────────────────────────────────────────────────────
function computeScore(data, patientAge) {
  const checks = [];
  const bmi = parseFloat(data.bmi?.value ?? data.bmi);
  const sys = parseFloat(data.bloodPressure?.systolic ?? data.systolic);
  const dia = parseFloat(data.bloodPressure?.diastolic ?? data.diastolic);
  const spo2 = parseFloat(data.oxygenSaturation?.value ?? data.oxygenSaturation);
  const pulse = parseFloat(data.pulseRate?.value ?? data.pulseRate ?? data.heartRate?.value);
  const hemo = parseFloat(data.hemoglobin?.value);
  const glucose = parseFloat(data.bloodGlucose?.random ?? data.bloodGlucose?.fasting);
  const fat = parseFloat(data.bodyComposition?.bodyFatPercentage ?? data.bodyFatPercentage);
  const visceral = parseFloat(data.bodyComposition?.visceralFat ?? data.visceralFat);
  const fev1Fvc = parseFloat(data.spirometry?.fev1Fvc ?? data.fev1Fvc);

  if (!isNaN(bmi))     checks.push(bmi >= 18.5 && bmi <= 24.9);
  if (!isNaN(sys))     checks.push(sys >= 90 && sys <= 120);
  if (!isNaN(dia))     checks.push(dia >= 60 && dia <= 80);
  if (!isNaN(spo2))    checks.push(spo2 >= 95);
  if (!isNaN(pulse))   checks.push(pulse >= 60 && pulse <= 100);
  if (!isNaN(hemo))    checks.push(hemo >= 12 && hemo <= 17);
  if (!isNaN(glucose)) checks.push(glucose >= 80 && glucose <= 140);
  if (!isNaN(fat))     checks.push(fat >= 10 && fat <= 25);
  if (!isNaN(visceral))checks.push(visceral >= 1 && visceral <= 12);
  if (!isNaN(fev1Fvc)) checks.push(fev1Fvc >= 75 && fev1Fvc <= 95);

  if (checks.length === 0) return null;
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

// ── Recommendations ───────────────────────────────────────────────────────────
function buildRecommendations(data) {
  const dos = [];
  const donts = [];
  const priorities = [];

  const bmi     = parseFloat(data.bmi?.value ?? data.bmi);
  const sys     = parseFloat(data.bloodPressure?.systolic ?? data.systolic);
  const spo2    = parseFloat(data.oxygenSaturation?.value ?? data.oxygenSaturation);
  const hemo    = parseFloat(data.hemoglobin?.value);
  const glucose = parseFloat(data.bloodGlucose?.random ?? data.bloodGlucose?.fasting);
  const fat     = parseFloat(data.bodyComposition?.bodyFatPercentage ?? data.bodyFatPercentage);
  const muscle  = parseFloat(data.bodyComposition?.muscleMass ?? data.muscleMass);
  const visceral= parseFloat(data.bodyComposition?.visceralFat ?? data.visceralFat);
  const fev1    = parseFloat(data.spirometry?.fev1 ?? data.fev1);
  const water   = parseFloat(data.bodyComposition?.bodyWater ?? data.bodyWaterPercentage);

  if (!isNaN(bmi) && bmi > 24.9) {
    priorities.push({ label: 'Weight Management', detail: `Reduce BMI from ${parseFloat(bmi).toFixed(1)} to 18.5–24.9 kg/m²`, color: '#d97706' });
    dos.push({ title: 'Balanced Diet', detail: 'Focus on whole grains, vegetables, lean proteins. Limit calorie intake.' });
    dos.push({ title: 'Cardio Exercise', detail: '30-45 mins brisk walk or jog, 4-5 days/week to burn excess fat.' });
    donts.push({ title: 'Avoid Processed Foods', detail: 'Cut packaged snacks, fast food, and fried carbs.' });
    donts.push({ title: 'Cut Added Sugar', detail: 'Skip sugary drinks and desserts. They spike insulin and lead to fat gain.' });
  }

  if (!isNaN(fat) && fat > 25) {
    priorities.push({ label: 'Body Fat Reduction', detail: `Reduce body fat from ${parseFloat(fat).toFixed(1)}% to below 25%`, color: '#d97706' });
    dos.push({ title: 'Strength Training', detail: '2-3 times/week to build muscle and support fat loss.' });
    donts.push({ title: "Don't Stay Inactive", detail: 'Move every 30 mins, avoid sitting for hours. Keeps metabolism active.' });
  }

  if (!isNaN(muscle) && muscle < 38) {
    priorities.push({ label: 'Muscle Mass', detail: `Increase muscle from ${parseFloat(muscle).toFixed(1)} kg to above 38 kg`, color: '#d97706' });
    dos.push({ title: 'Increase Protein', detail: 'Focus on lean meats, tofu, legumes. 1.6-1.8g per kg body weight.' });
    dos.push({ title: 'Resistance Training', detail: 'Progressive overload with weights 3x/week for muscle growth.' });
  }

  if (!isNaN(sys) && sys > 120) {
    priorities.push({ label: 'Blood Pressure', detail: `Reduce systolic from ${Math.round(sys)} to 90–120 mmHg`, color: '#dc2626' });
    dos.push({ title: 'Reduce Sodium Intake', detail: 'Limit salt to under 2300mg/day. Avoid pickles, processed meats.' });
    donts.push({ title: 'Limit Alcohol', detail: 'Alcohol raises blood pressure. Keep to minimum or avoid entirely.' });
    donts.push({ title: 'No Crash Diets', detail: 'Extreme calorie cuts harm metabolism and can spike BP.' });
  }

  if (!isNaN(glucose) && glucose > 140) {
    priorities.push({ label: 'Blood Sugar', detail: `Bring glucose from ${Math.round(glucose)} mg/dL to 80–140 mg/dL`, color: '#dc2626' });
    dos.push({ title: 'Low Glycemic Diet', detail: 'Choose brown rice, oats, leafy greens. Avoid refined carbs.' });
    donts.push({ title: 'Avoid Sugary Drinks', detail: 'Sodas, juices spike blood sugar rapidly. Drink water instead.' });
  }

  if (!isNaN(spo2) && spo2 < 95) {
    priorities.push({ label: 'Oxygen Saturation', detail: `Improve SpO₂ from ${Math.round(spo2)}% to above 95%`, color: '#dc2626' });
    dos.push({ title: 'Breathing Exercises', detail: 'Practice deep diaphragmatic breathing and pranayama daily.' });
    donts.push({ title: 'Avoid Smoking', detail: 'Smoking severely reduces lung capacity and oxygen saturation.' });
  }

  if (!isNaN(fev1) && fev1 < 3.5) {
    priorities.push({ label: 'Lung Function', detail: `Improve FEV1 from ${parseFloat(fev1).toFixed(2)}L to above 3.5L`, color: '#d97706' });
    dos.push({ title: 'Aerobic Activity', detail: 'Swimming, cycling improve lung capacity over time.' });
  }

  if (!isNaN(water) && water < 50) {
    dos.push({ title: 'Stay Hydrated', detail: 'Drink 3-4 liters of water each day. Keeps digestion smooth and removes toxins.' });
  }

  if (!isNaN(hemo) && hemo < 12) {
    priorities.push({ label: 'Hemoglobin', detail: `Raise hemoglobin from ${parseFloat(hemo).toFixed(1)} to 12–17 g/dL`, color: '#dc2626' });
    dos.push({ title: 'Iron-Rich Diet', detail: 'Eat spinach, lentils, red meat, fortified cereals to boost iron levels.' });
    donts.push({ title: 'Avoid Tea/Coffee with Meals', detail: 'Tannins in tea/coffee block iron absorption. Have them between meals.' });
  }

  if (!isNaN(visceral) && visceral > 12) {
    donts.push({ title: 'Avoid Late Night Eating', detail: 'Late meals increase visceral fat storage. Finish dinner by 8pm.' });
  }

  const hasAnyData = [bmi, sys, spo2, hemo, glucose, fat, muscle, visceral, fev1, water].some(v => !isNaN(v));
  if (hasAnyData) {
    dos.push({ title: 'Prioritize Sleep', detail: 'Aim for 7-8 hours of quality sleep. Recovery and focus improve with rest.' });
    donts.push({ title: 'No Crash Diets', detail: 'Avoid extreme calorie cuts — it harms metabolism and muscle mass.' });
  }

  const uniqueDos = dos.filter((d, i, arr) => arr.findIndex(x => x.title === d.title) === i).slice(0, 5);
  const uniqueDonts = donts.filter((d, i, arr) => arr.findIndex(x => x.title === d.title) === i).slice(0, 5);
  const uniquePriorities = priorities.slice(0, 3);

  return { dos: uniqueDos, donts: uniqueDonts, priorities: uniquePriorities };
}

export function generateReport(reportData) {
  const { patient, session, testData, legacyVitals, reportGeneratedAt, maskPii = true } = reportData;
  const data = normalizeData({ ...(testData || {}), ...(legacyVitals || {}) });
  const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/eros-wellness-logo.png';

  const displayName = maskPii
    ? (patient.name || 'N/A').split(' ').map(w => w.length <= 1 ? w : w[0] + '*'.repeat(w.length - 1)).join(' ')
    : (patient.name || 'N/A');
  const displayMobile = maskPii
    ? (() => {
        const s = String(patient.mobile || '').replace(/\s/g, '');
        if (!s) return '-';
        if (s.length <= 4) return '*'.repeat(s.length);
        return s.slice(0, 2) + '*'.repeat(s.length - 4) + s.slice(-2);
      })()
    : (patient.mobile || '-');

  const hasVal = (v) => v !== null && v !== undefined && v !== '';
  const fv = (obj, fallback) => {
    const v = obj?.value ?? obj ?? fallback ?? null;
    return v;
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const formatDateShort = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const patientAge = patient.age || (patient.dateOfBirth
    ? Math.floor((Date.now() - new Date(patient.dateOfBirth)) / 31557600000) : null);

  const sessionDate = session?.completedAt || session?.startedAt || data.testDate || reportGeneratedAt;
  const overallScore = computeScore(data, patientAge);
  const recommendations = buildRecommendations(data);

  // Extract values
  const heightVal   = fv(data.height);
  const weightVal   = fv(data.weight);
  const bmiVal      = fv(data.bmi);
  const fatVal      = fv(data.bodyComposition?.bodyFatPercentage) ?? data.bodyFatPercentage;
  const subcutVal   = fv(data.bodyComposition?.subcutaneousFat) ?? data.subcutaneousFat;
  const visceralVal = fv(data.bodyComposition?.visceralFat) ?? data.visceralFat;
  const boneMassVal = fv(data.bodyComposition?.boneMass) ?? data.boneMass;
  const bmrVal      = fv(data.bodyComposition?.bmr) ?? data.bmr;
  const metAgeVal   = fv(data.bodyComposition?.metabolicAge) ?? data.metabolicAge;
  const muscleVal   = fv(data.bodyComposition?.muscleMass) ?? data.muscleMass;
  const waterVal    = fv(data.bodyComposition?.bodyWater) ?? data.bodyWaterPercentage;
  const proteinVal  = fv(data.bodyComposition?.proteinPercentage) ?? data.proteinPercentage;
  const skelMuscleVal = fv(data.bodyComposition?.skeletalMusclePercentage) ?? data.skeletalMusclePercentage;
  const bodyScoreVal  = fv(data.bodyComposition?.bodyScore) ?? data.bodyScore;

  const sysVal    = data.bloodPressure?.systolic ?? data.systolic;
  const diaVal    = data.bloodPressure?.diastolic ?? data.diastolic;
  const pulseVal  = fv(data.pulseRate) ?? fv(data.heartRate);
  const spo2Val   = fv(data.oxygenSaturation);
  const tempVal   = fv(data.bodyTemperature);
  const respRateVal = fv(data.respiratoryRate);

  const hemoVal    = data.hemoglobin?.value ?? data.hemoglobin;
  const glucoseVal = data.bloodGlucose?.random ?? data.bloodGlucose?.fasting;

  const fev1Val    = data.spirometry?.fev1 ?? data.fev1;
  const fev1FvcVal = data.spirometry?.fev1Fvc ?? data.fev1Fvc;
  const fvcVal     = data.spirometry?.fvc ?? data.fvc;
  const fef25Val   = data.spirometry?.fef25 ?? data.fef25;
  const fef50Val   = data.spirometry?.fef50 ?? data.fef50;
  const fef75Val   = data.spirometry?.fef75 ?? data.fef75;
  const fef2575Val = data.spirometry?.fef2575 ?? data.fef2575;
  const pefVal     = data.spirometry?.pef ?? data.pef;

  const hasBodyComp    = hasVal(fatVal) || hasVal(muscleVal) || hasVal(bmiVal) || hasVal(heightVal);
  const hasCardio      = hasVal(sysVal) || hasVal(pulseVal) || hasVal(spo2Val) || hasVal(respRateVal);
  const hasBloodChem   = hasVal(glucoseVal) || hasVal(hemoVal) || hasVal(waterVal);
  const hasRespiratory = hasVal(fev1Val) || hasVal(fvcVal) || hasVal(pefVal) || hasVal(fef50Val) || hasVal(fef75Val) || hasVal(tempVal);
  const hasInfectious  = hasVal(data.malariaTest) || hasVal(data.covidTest);
  const isNotMale      = !patient.gender || patient.gender.toLowerCase() !== 'male';
  const hasPregnancy   = isNotMale && hasVal(data.pregnancyTest);

  // ── Zone definitions (from clinical notes) ────────────────────────────────
  const ageBound = patientAge || 35;

  const zoneWeight = [
    { label: 'Under wt',   textColor: '#0891b2', bgColor: '#e0f2fe', max: 50 },
    { label: 'Optimum',    textColor: '#16a34a', bgColor: '#dcfce7', max: 85 },
    { label: 'Overweight', textColor: '#d97706', bgColor: '#fef3c7', max: 100 },
    { label: 'Obesity',    textColor: '#dc2626', bgColor: '#fee2e2', max: 180 }
  ];
  const zoneBMI = [
    { label: 'Under wt',   textColor: '#0891b2', bgColor: '#e0f2fe', max: 18.5 },
    { label: 'Optimum',    textColor: '#16a34a', bgColor: '#dcfce7', max: 24.9 },
    { label: 'Overweight', textColor: '#d97706', bgColor: '#fef3c7', max: 29.9 },
    { label: 'Obese',      textColor: '#dc2626', bgColor: '#fee2e2', max: 45 }
  ];
  const zoneBoneMass = [
    { label: 'Very Low', textColor: '#991b1b', bgColor: '#fecaca', max: 1.8 },
    { label: 'Low',      textColor: '#dc2626', bgColor: '#fee2e2', max: 2.5 },
    { label: 'Optimum',  textColor: '#16a34a', bgColor: '#dcfce7', max: 3.5 },
    { label: 'High',     textColor: '#d97706', bgColor: '#fef3c7', max: 6 }
  ];
  const zoneMetabolicAge = [
    { label: 'Low',     textColor: '#16a34a', bgColor: '#dcfce7', max: Math.max(15, ageBound - 5) },
    { label: 'Optimum', textColor: '#16a34a', bgColor: '#d1fae5', max: ageBound + 5 },
    { label: 'High',    textColor: '#dc2626', bgColor: '#fee2e2', max: 80 }
  ];
  const zoneMuscle = [
    { label: 'Low',     textColor: '#dc2626', bgColor: '#fee2e2', max: 38 },
    { label: 'Optimum', textColor: '#16a34a', bgColor: '#dcfce7', max: 48 },
    { label: 'High',    textColor: '#16a34a', bgColor: '#d1fae5', max: 80 }
  ];
  const zoneProtein = [
    { label: 'Low',     textColor: '#dc2626', bgColor: '#fee2e2', max: 16 },
    { label: 'Optimum', textColor: '#16a34a', bgColor: '#dcfce7', max: 20 },
    { label: 'High',    textColor: '#d97706', bgColor: '#fef3c7', max: 30 }
  ];
  const zoneSkeletal = [
    { label: 'Low',     textColor: '#dc2626', bgColor: '#fee2e2', max: 40 },
    { label: 'Optimum', textColor: '#16a34a', bgColor: '#dcfce7', max: 55 },
    { label: 'High',    textColor: '#16a34a', bgColor: '#d1fae5', max: 70 }
  ];
  const zoneBodyScore = [
    { label: 'Low',     textColor: '#dc2626', bgColor: '#fee2e2', max: 60 },
    { label: 'Optimum', textColor: '#16a34a', bgColor: '#dcfce7', max: 85 },
    { label: 'High',    textColor: '#16a34a', bgColor: '#d1fae5', max: 100 }
  ];
  // Systolic BP: Low | Opt | Normal | High-Normal | High
  const zoneSysBP = [
    { label: 'Low',       textColor: '#0891b2', bgColor: '#e0f2fe', max: 90 },
    { label: 'Optimal',   textColor: '#16a34a', bgColor: '#d1fae5', max: 100 },
    { label: 'Normal',    textColor: '#16a34a', bgColor: '#dcfce7', max: 120 },
    { label: 'High-Norm', textColor: '#d97706', bgColor: '#fef3c7', max: 140 },
    { label: 'High',      textColor: '#dc2626', bgColor: '#fee2e2', max: 200 }
  ];
  // Diastolic BP: Low | Opt | Normal | High-Normal | High
  const zoneDiaBP = [
    { label: 'Low',       textColor: '#0891b2', bgColor: '#e0f2fe', max: 60 },
    { label: 'Optimal',   textColor: '#16a34a', bgColor: '#d1fae5', max: 70 },
    { label: 'Normal',    textColor: '#16a34a', bgColor: '#dcfce7', max: 80 },
    { label: 'High-Norm', textColor: '#d97706', bgColor: '#fef3c7', max: 90 },
    { label: 'High',      textColor: '#dc2626', bgColor: '#fee2e2', max: 130 }
  ];
  const zonePulse = [
    { label: 'Low',     textColor: '#0891b2', bgColor: '#e0f2fe', max: 60 },
    { label: 'Optimum', textColor: '#16a34a', bgColor: '#dcfce7', max: 100 },
    { label: 'High',    textColor: '#dc2626', bgColor: '#fee2e2', max: 180 }
  ];

  // Respiratory grade summary
  const respGrade = hasRespiratory ? computeRespiratoryGrade(fev1FvcVal, fvcVal, fev1Val) : null;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Health Report – ${displayName}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #1e293b; background: #f0f4f8; }

  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
    body { background: #fff; }
    .no-print { display: none !important; }
    .page { box-shadow: none; margin: 0; border-radius: 0; max-width: 100%; overflow: visible !important; }
    .grid2 {
      display: block !important;
      -webkit-column-count: 2;
      column-count: 2;
      -webkit-column-gap: 12px;
      column-gap: 12px;
    }
    .mc { break-inside: avoid; page-break-inside: avoid; display: inline-block; width: 100%; margin-bottom: 12px; }
    .priority-item { break-inside: avoid; page-break-inside: avoid; }
    .rec-grid { display: block !important; -webkit-column-count: 2; column-count: 2; column-gap: 16px; }
    .rec-grid > div { break-inside: avoid; page-break-inside: avoid; display: inline-block; width: 100%; }
    .disclaimer { break-inside: avoid !important; page-break-inside: avoid !important; display: block !important; background: #fffbeb !important; border-top: 2px solid #d97706 !important; border-bottom: 2px solid #d97706 !important; }
    .resp-overall { break-inside: avoid; page-break-inside: avoid; }
  }

  .page { max-width: 900px; margin: 24px auto; background: #fff; border-radius: 12px; box-shadow: 0 4px 32px rgba(0,0,0,0.10); }

  /* ── Header ── */
  .header { padding: 20px 32px; display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 1px solid #e2e8f0; }
  .header-logo { height: 80px; width: auto; object-fit: contain; }
  .header-right { text-align: right; }
  .header-right .hr-title { font-size: 17px; font-weight: 800; color: #005B8F; }
  .header-right .hr-title span { color: #dc2626; }
  .header-right .hr-sub { font-size: 12px; color: #64748b; margin-top: 2px; }
  .header-right .hr-meta { font-size: 11px; color: #005B8F; font-weight: 600; margin-top: 2px; }

  /* ── Patient Info Row ── */
  .patient-row { display: grid; grid-template-columns: repeat(6, 1fr); border-bottom: 2px solid #00A8E8; }
  .patient-cell { padding: 14px 16px; border-right: 1px solid #e2e8f0; }
  .patient-cell:last-child { border-right: none; }
  .patient-cell .pc-label { font-size: 9px; font-weight: 800; color: #005B8F; letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 4px; }
  .patient-cell .pc-value { font-size: 13px; font-weight: 700; color: #0f172a; }
  .score-circle { width: 40px; height: 40px; border-radius: 50%; background: #00A8E8; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 800; color: #fff; margin-top: 2px; }

  /* ── Section Header ── */
  .section { margin: 0; padding: 20px 28px; border-bottom: 1px solid #f1f5f9; }
  .section:last-child { border-bottom: none; }
  .section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
  .section-icon { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; }
  .section-title { font-size: 14px; font-weight: 700; color: #1e293b; flex: 1; }
  .section-line { flex: 1; height: 1px; background: #e2e8f0; }

  /* ── 2-Column Grid ── */
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

  /* ── Metric Card ── */
  .mc { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; }
  .mc-name { font-size: 13px; font-weight: 700; color: #0f172a; }
  .mc-desc { font-size: 11px; font-weight: 700; color: #005B8F; margin-top: 2px; margin-bottom: 10px; }
  .mc-row { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 10px; }
  .mc-rlabel { font-size: 10px; font-weight: 700; color: #005B8F; margin-bottom: 2px; }
  .mc-value { font-size: 22px; font-weight: 800; color: #0f172a; line-height: 1; }
  .mc-unit { font-size: 13px; font-weight: 400; color: #64748b; }
  .mc-range { text-align: right; }
  .mc-range-val { font-size: 11px; color: #64748b; font-weight: 500; }

  /* ── Simple Progress Bar ── */
  .bar-track { position: relative; height: 8px; background: #e2e8f0; border-radius: 4px; margin-bottom: 10px; overflow: visible; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .bar-fill { position: absolute; left: 0; top: 0; height: 100%; border-radius: 4px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .bar-dot { position: absolute; top: 50%; transform: translate(-50%, -50%); width: 13px; height: 13px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.35); z-index: 2; }

  /* ── Metric Status ── */
  .mc-status { font-size: 11px; font-weight: 700; text-align: right; }

  /* ── Respiratory Overall Banner ── */
  .resp-overall { display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-radius: 10px; margin-bottom: 16px; border: 1.5px solid; }

  /* ── Recommendations ── */
  .rec-section { padding: 20px 28px; background: #f8fafc; }
  .rec-header { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
  .rec-icon { width: 32px; height: 32px; border-radius: 8px; background: #00A8E8; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 16px; }
  .rec-title { font-size: 14px; font-weight: 800; color: #0f172a; }
  .rec-sub { font-size: 12px; color: #64748b; margin-bottom: 14px; }
  .priority-label { font-size: 11px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 8px; }
  .priority-item { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; margin-bottom: 8px; }
  .priority-dot { width: 8px; height: 8px; border-radius: 50%; background: #d97706; flex-shrink: 0; }
  .priority-name { font-size: 13px; font-weight: 700; color: #92400e; }
  .priority-detail { font-size: 11px; color: #a16207; margin-top: 1px; }
  .rec-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 16px; }
  .rec-col-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; font-size: 13px; font-weight: 800; }
  .rec-col-header span { font-size: 18px; }
  .rec-item { margin-bottom: 10px; }
  .rec-item-title { font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
  .rec-item-detail { font-size: 11px; color: #64748b; line-height: 1.5; }

  /* ── Disclaimer ── */
  .disclaimer {
    margin: 0; padding: 18px 28px;
    background: #fffbeb !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    border-top: 2px solid #d97706; border-bottom: 2px solid #d97706;
    break-inside: avoid; page-break-inside: avoid;
  }
  .disclaimer-title { font-size: 11px; font-weight: 800; color: #92400e; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
  .disclaimer-text { font-size: 10.5px; color: #1e293b; line-height: 1.75; }

  /* ── Footer ── */
  .footer { padding: 14px 32px; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #e2e8f0; font-size: 11px; color: #64748b; flex-wrap: wrap; gap: 8px; }
  .footer strong { color: #0f172a; }

  /* ── Print Button ── */
  .print-bar { text-align: center; padding: 16px; background: #f8fafc; }
  .print-btn { background: linear-gradient(135deg, #00A8E8, #005B8F); color: #fff; border: none; padding: 11px 32px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; margin: 0 6px; }
  .print-btn:hover { opacity: 0.88; }
  .print-btn.outline { background: #fff; color: #005B8F; border: 2px solid #00A8E8; }
  .na { color: #94a3b8; font-style: italic; }
</style>
</head>
<body>

<div class="print-bar no-print">
  <button class="print-btn" onclick="window.print()">&#128438; Print / Save as PDF</button>
  <button class="print-btn outline" onclick="window.close()">&#10005; Close</button>
</div>

<div class="page">

  <!-- Header -->
  <div class="header">
    <img src="${LOGO_URL}" class="header-logo" alt="Eros Innovation" />
    <div class="header-right">
      <div class="hr-title"><span>&#9829;</span> Health Report</div>
      <div class="hr-sub">Health Kiosk Management System</div>
      <div class="hr-meta">Report ID: ${patient.id || patient._id || 'N/A'}</div>
      <div class="hr-meta">Generated: ${formatDateShort(reportGeneratedAt)}</div>
    </div>
  </div>

  <!-- Patient Info Row -->
  <div class="patient-row">
    <div class="patient-cell">
      <div class="pc-label">Patient Name</div>
      <div class="pc-value">${displayName}</div>
      <div style="font-size:11px;color:#64748b;margin-top:2px">+91 ${displayMobile}</div>
    </div>
    <div class="patient-cell">
      <div class="pc-label">Patient ID</div>
      <div class="pc-value">${patient.qrCode || (String(patient._id || '').slice(-8).toUpperCase()) || 'N/A'}</div>
    </div>
    <div class="patient-cell">
      <div class="pc-label">Age / Gender</div>
      <div class="pc-value">${patientAge ? patientAge + ' Yrs' : 'Not recorded'} / ${patient.gender ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1) : 'N/A'}</div>
    </div>
    <div class="patient-cell">
      <div class="pc-label">Location</div>
      <div class="pc-value">${session?.location?.name || patient.location?.name || 'N/A'}</div>
    </div>
    <div class="patient-cell">
      <div class="pc-label">Collection Date</div>
      <div class="pc-value" style="font-size:11px">${formatDateShort(sessionDate)}</div>
    </div>
    <div class="patient-cell">
      <div class="pc-label">Health Score</div>
      ${overallScore !== null
        ? `<div class="score-circle">${overallScore}</div>`
        : `<div class="pc-value">N/A</div>`}
    </div>
  </div>

  <!-- ── BODY COMPOSITION ── -->
  ${hasBodyComp ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon" style="background:#e0f7ff;color:#0891b2">&#9098;</div>
      <div class="section-title">Body Composition</div>
      <div class="section-line"></div>
    </div>
    <div class="grid2">
      ${actualValueCard('Height', 'Body height measurement', heightVal, 'cm', 100, 220)}
      ${metricCard('Weight', 'Body weight measurement', weightVal, 'kg', 50, 85, 20, 180, null, false, false, zoneWeight)}
      ${metricCard('BMI', 'Body Mass Index', bmiVal, 'kg/m²', 18.5, 24.9, 10, 45, null, false, false, zoneBMI)}
      ${metricCard('Fat %', 'Body fat percentage', fatVal, '%', 10, 25, 0, 50)}
      ${metricCard('Subcutaneous Fat', 'Fat under the skin', subcutVal, '%', 10, 20, 0, 40)}
      ${metricCard('Visceral Fat %', 'Fat around internal organs', visceralVal, '%', 1, 12, 0, 30)}
      ${metricCard('Bone Mass', 'Bone mineral content', boneMassVal, 'kg', 2.5, 3.5, 1, 6, null, false, false, zoneBoneMass)}
      ${actualValueCard('Basal Metabolic Rate', 'Calories burned at rest', bmrVal, 'kcal', 500, 3000)}
      ${metricCard('Metabolic Age', 'Age based on metabolism', metAgeVal, 'years', Math.max(15, ageBound - 5), ageBound + 5, 15, 80, null, false, false, zoneMetabolicAge)}
      ${metricCard('Muscle Mass', 'Skeletal muscle mass', muscleVal, 'kg', 38, 48, 10, 80, null, false, false, zoneMuscle)}
      ${metricCard('Protein %', 'Body protein percentage', proteinVal, '%', 16, 20, 5, 30, null, false, false, zoneProtein)}
      ${metricCard('Skeletal Muscle %', 'Skeletal muscle percentage', skelMuscleVal, '%', 40, 55, 20, 70, null, false, false, zoneSkeletal)}
      ${metricCard('Body Score', 'Overall body composition score', bodyScoreVal, '', 60, 85, 0, 100, null, false, false, zoneBodyScore)}
    </div>
  </div>` : ''}

  <!-- ── CARDIOVASCULAR ── -->
  ${hasCardio ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon" style="background:#fef2f2;color:#dc2626">&#9825;</div>
      <div class="section-title">Cardiovascular</div>
      <div class="section-line"></div>
    </div>
    <div class="grid2">
      ${metricCard('BP - Systolic', 'Upper blood pressure reading', sysVal, 'mmHg', 90, 120, 60, 200, null, false, false, zoneSysBP)}
      ${metricCard('BP - Diastolic', 'Lower blood pressure reading', diaVal, 'mmHg', 60, 80, 40, 130, null, false, false, zoneDiaBP)}
      ${metricCard('Pulse Rate', 'Heart beats per minute', pulseVal, 'bpm', 60, 100, 30, 180, null, false, false, zonePulse)}
      ${metricCard('SPO2', 'Blood oxygen saturation', spo2Val, '%', 95, 100, 70, 100)}
      ${metricCard('Respiratory Rate', 'Breathing rate per minute', respRateVal, 'breaths/min', 12, 20, 5, 40)}
    </div>
  </div>` : ''}

  <!-- ── BLOOD CHEMISTRY ── -->
  ${hasBloodChem ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon" style="background:#fff7ed;color:#d97706">&#9670;</div>
      <div class="section-title">Blood Chemistry</div>
      <div class="section-line"></div>
    </div>
    <div class="grid2">
      ${hasVal(glucoseVal) ? metricCard('Blood Sugar - Random', 'Glucose level at any time', glucoseVal, 'mg/dL', 80, 140, 40, 300) : ''}
      ${hasVal(hemoVal) ? metricCard('Hemoglobin', 'Oxygen-carrying protein in blood', hemoVal, 'g/dL', 12, 17, 6, 22) : ''}
      ${hasVal(waterVal) ? metricCard('Hydration', 'Body water percentage', waterVal, '%', 50, 65, 30, 80) : ''}
    </div>
  </div>` : ''}

  <!-- ── RESPIRATORY FUNCTION ── -->
  ${hasRespiratory ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon" style="background:#f0fdf4;color:#16a34a">&#8651;</div>
      <div class="section-title">Respiratory Function</div>
      <div class="section-line"></div>
    </div>

    ${respGrade ? `
    <div class="resp-overall" style="background:${respGrade.bg};border-color:${respGrade.color}">
      <div style="width:48px;height:48px;border-radius:50%;background:${respGrade.color};display:flex;align-items:center;justify-content:center;flex-shrink:0;-webkit-print-color-adjust:exact;print-color-adjust:exact">
        <span style="font-size:22px">&#8651;</span>
      </div>
      <div style="flex:1">
        <div style="font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.7px">Overall Lung Function Rate</div>
        <div style="font-size:16px;font-weight:800;color:${respGrade.color};margin:2px 0">${respGrade.label}</div>
        <div style="font-size:11px;color:#475569">${respGrade.detail}</div>
      </div>
      ${hasVal(fev1FvcVal) ? `
      <div style="text-align:right;flex-shrink:0">
        <div style="font-size:10px;font-weight:700;color:#64748b">FEV1/FVC Score</div>
        <div style="font-size:22px;font-weight:800;color:${respGrade.color}">${parseFloat(fev1FvcVal).toFixed(1)}<span style="font-size:12px;font-weight:400">%</span></div>
        <div style="font-size:10px;color:#64748b">Expected ≥ 75%</div>
      </div>` : ''}
    </div>` : ''}

    <div class="grid2">
      ${metricCard('FVC', 'Forced vital capacity', fvcVal, 'L', 3.5, 5.0, 0, 8, null, true)}
      ${metricCard('FEV1', 'Forced expiratory volume in 1 second', fev1Val, 'L', 3.0, 4.5, 0, 7, null, true)}
      ${metricCard('FEV1/FVC %', 'FEV1 to FVC ratio — overall lung function', fev1FvcVal, '%', 75, 100, 0, 110, null, false)}
      ${metricCard('FEF 25%', 'Forced expiratory flow at 25%', fef25Val, 'L/s', 3.5, 5, 0, 10, null, true)}
      ${metricCard('FEF 50%', 'Forced expiratory flow at 50%', fef50Val, 'L/s', 3.0, 5, 0, 10, null, true)}
      ${metricCard('FEF 75%', 'Forced expiratory flow at 75%', fef75Val, 'L/s', 1.5, 3, 0, 7, null, true)}
      ${metricCard('FEF 25-75%', 'Mean forced expiratory flow 25–75%', fef2575Val, 'L/s', 2.5, 4.5, 0, 8, null, true)}
      ${metricCard('PEF', 'Peak expiratory flow rate', pefVal, 'L/s', 6, 10, 0, 15, null, true)}
      ${metricCard('Temperature', 'Body temperature', tempVal, '°F', 97, 99, 94, 104)}
    </div>
  </div>` : ''}

  <!-- ── INFECTIOUS DISEASES ── -->
  ${hasInfectious ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon" style="background:#fff1f2;color:#e11d48">&#128299;</div>
      <div class="section-title">Infectious Diseases</div>
      <div class="section-line"></div>
    </div>
    <div class="grid2">
      ${labCard('Malaria', 'Malaria parasite rapid test', data.malariaTest)}
      ${labCard('COVID-19 AG', 'COVID-19 antigen rapid test', data.covidTest)}
    </div>
  </div>` : ''}

  <!-- ── PREGNANCY TEST ── -->
  ${hasPregnancy ? `
  <div class="section">
    <div class="section-header">
      <div class="section-icon" style="background:#fdf4ff;color:#a21caf">&#9792;</div>
      <div class="section-title">Pregnancy Test</div>
      <div class="section-line"></div>
    </div>
    <div class="grid2">
      ${labCard('Pregnancy Test', 'Human chorionic gonadotropin (hCG) test', data.pregnancyTest)}
    </div>
  </div>` : ''}

  <!-- ── RECOMMENDATIONS ── -->
  ${(recommendations.dos.length > 0 || recommendations.priorities.length > 0) ? `
  <div class="rec-section">
    <div class="rec-header">
      <div class="rec-icon">&#128196;</div>
      <div>
        <div class="rec-title">Personalized Recommendations</div>
      </div>
    </div>
    <div class="rec-sub">Based on your results, here are key actions to improve your health metrics</div>

    ${recommendations.priorities.length > 0 ? `
    <div class="priority-label">Priority Areas to Focus</div>
    ${recommendations.priorities.map(p => `
    <div class="priority-item">
      <div>
        <div class="priority-name">&#9888; ${p.label}</div>
        <div class="priority-detail">${p.detail}</div>
      </div>
    </div>`).join('')}` : ''}

    <div class="rec-grid">
      <div>
        <div class="rec-col-header" style="color:#16a34a"><span>&#9745;</span> Things to DO</div>
        ${recommendations.dos.map(d => `
        <div class="rec-item">
          <div class="rec-item-title">${d.title}</div>
          <div class="rec-item-detail">${d.detail}</div>
        </div>`).join('')}
      </div>
      <div>
        <div class="rec-col-header" style="color:#dc2626"><span>&#9746;</span> Things to Avoid</div>
        ${recommendations.donts.map(d => `
        <div class="rec-item">
          <div class="rec-item-title">${d.title}</div>
          <div class="rec-item-detail">${d.detail}</div>
        </div>`).join('')}
      </div>
    </div>
  </div>` : ''}

  <!-- Patient Consent -->
  <div style="margin-bottom:16px;padding:16px 20px;background:#f0fdf4;border-top:2px solid #16a34a;border-bottom:2px solid #16a34a;break-inside:avoid;page-break-inside:avoid">
    <div style="font-size:11px;font-weight:800;color:#15803d;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px">&#10003; Patient Consent Recorded</div>
    <div style="font-size:10.5px;color:#1e293b;line-height:1.75">
      <div style="margin-bottom:4px">&#10003;&nbsp; I have read and understood the information provided to me.</div>
      <div style="margin-bottom:4px">&#10003;&nbsp; I voluntarily agree to participate in the study.</div>
      <div style="margin-bottom:4px">&#10003;&nbsp; I understand that AI face scan outputs are screening and wellness indicators and not a substitute for clinical diagnosis.</div>
      <div style="margin-bottom:4px">&#10003;&nbsp; I understand that the accuracy of the face scan can vary, and that concerning results will be confirmed by standard measurements.</div>
      <div>&#10003;&nbsp; I consent to the recording and secure storage of a facial video and the measurements derived from it, for this research only.</div>
    </div>
  </div>

  <!-- Disclaimer -->
  <div class="disclaimer" style="break-before:auto;page-break-before:auto">
    <div class="disclaimer-title">⚠ Confidential Medical Report Disclaimer</div>
    <div class="disclaimer-text">
      This report has been prepared based on the specimen(s), data, and/or information provided at the time of evaluation and is intended strictly for clinical reference and informational purposes only. The findings and results contained herein must be interpreted in conjunction with the patient's clinical history, physical examination, and other diagnostic parameters by a qualified and registered medical practitioner. While every effort has been made to ensure the accuracy and reliability of the results, the reporting laboratory/organization does not assume responsibility for variations that may arise due to pre-analytical, analytical, or post-analytical factors including, but not limited to, sample collection, handling, biological variability, or technical limitations. Reference ranges provided are indicative and may vary depending on demographic and physiological factors. This report shall not be considered as a standalone basis for diagnosis, treatment, or medical decision-making. Any medical action taken solely on the basis of this report shall be at the discretion and responsibility of the consulting healthcare provider. All patient-related information is maintained with strict confidentiality in accordance with applicable Indian laws, including the Information Technology Act and Digital Personal Data Protection regulations, and aligned with internationally accepted healthcare privacy standards. Unauthorized use, disclosure, or reproduction of this report is strictly prohibited and may be subject to legal action. The issuing entity shall not be held liable for any direct or indirect consequences arising from the use or interpretation of this report without appropriate medical consultation. This is a system-generated report and may not require a physical signature unless otherwise specified.
    </div>
  </div>

  <!-- Footer -->
  <div class="footer">
    <div><strong>Eros Innovation</strong> &nbsp;|&nbsp; Health Kiosk System v2.0<br>Report generated on ${formatDate(reportGeneratedAt)}</div>
    <div style="color:#94a3b8;text-align:center">System-generated report. No physical signature required unless specified.</div>
  </div>

</div>

<div class="print-bar no-print" style="margin-top:16px;margin-bottom:24px">
  <button class="print-btn" onclick="window.print()">&#128438; Print / Save as PDF</button>
  <button class="print-btn outline" onclick="window.close()">&#10005; Close</button>
</div>

</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  } else {
    alert('Popup was blocked. Please allow popups for this site to generate reports.');
  }
}
